import get from "lodash/get";
import { useEffect } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import useScrollToError from "../../../common/js/hooks/useScrollToError";
import Comments from "../comments/CommentsContainer";
import commentsConstants from "../comments/constants";
import commentsOperations from "../comments/ducks/operations";
import DocumentDrop from "../documents/DocumentDropContainer";
import searchOperations from "../search/ducks/operations";
import utils from "../utils/index";
import ConsultingDeal from "./ConsultingDeal";
import ConsultingDealToolbar from "./ConsultingDealToolbar";
import operations from "./ducks/operations";

const ConsultingDealContainer = (props) => {
    const {
        clearPropertyUpdate,
        clearStatusTracker,
        errors,
        getStatusTrackerData,
        handleClearDealState,
        voucher
    } = props;

    useEffect(() => {
        const abortController = new AbortController();
        getStatusTrackerData(voucher?.voucherKey, "VOUCHER", abortController.signal);
        return () => {
            abortController.abort();
            handleClearDealState();
            clearPropertyUpdate();
            clearStatusTracker();
        };
    }, []);

    const { id } = useParams();
    const { containerRef: consultingDealRef } = useScrollToError({});

    return (
        <div style={{ minWidth: "800px" }} ref={consultingDealRef}>
            <ConsultingDealToolbar {...props} />
            <DocumentDrop
                {...props}
                entityKey={id}
                type="voucher"
                documentTypes="consulting"
                formDisabled={false}
                errors={get(errors, "documents")}
                parentType="VOUCHER"
                stateCode={get(voucher, "deal.property.primaryAddress.city.state.stateCode", null)}
                entityStatus={voucher?.voucherStatus}
            />
            <ConsultingDeal {...props}/>
            <Comments parentRecordType={commentsConstants.VOUCHER_PARENT_RECORD_TYPE} parentRecordId={id} />
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        userProfile: state.authReducer.userProfile,
        valid: state.authReducer.validFormValues,
        snackbarOpen: state.authReducer.snackbarOpen,
        isSaving: state.utilsReducer.isSaving,
        saveError: state.voucherReducer.saveError ? state.voucherReducer.saveError : null,
        lastSaved: state.voucherReducer.lastSaved ? state.voucherReducer.lastSaved : null,
        brokerCommissions: state.voucherReducer.voucher.brokerCommissions,
        outsideSplits: state.voucherReducer.outsideSplits,
        outsideSplitModal: state.voucherReducer.outsideSplitModal,
        numComments: state.commentsReducer.comments.length,
        voucher: state.voucherReducer.voucher,
        errors: state.voucherReducer.errors,
        searchResults: state.utilsReducer.searchResults,
        autocompleteResults: state.searchReducer.autocompleteResults,
        snackbar: state.utilsReducer.snackbar,
        documentWarning: state.voucherReducer.documentWarning,
        confirmDialog: state.utilsReducer.confirmDialog,
        elementsSaving: state.utilsReducer.elementsSaving,
        updatedProperty: state.voucherReducer.updatedProperty,
        showDeleteDialog: state.voucherReducer.showDeleteDialog,
        deal: state.voucherReducer.voucher.deal,
        openModal: state.voucherReducer.modals,
        displayDeleteDialog: state.voucherReducer.displayDeleteDialog,
        displayNoDeleteDialog: state.voucherReducer.displayNoDeleteDialog,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getVoucher: (voucherKey) => {
            dispatch(operations.handleGetVoucher(voucherKey));
        },
        handleSubmit: (save, callback, bypassConfirm) => {
            if (bypassConfirm) {
                dispatch(utils.operations.toggleConfirm());
            }
            return dispatch(
                operations.handleSubmit(save, callback, "submit", {
                    validateDocs: true,
                    validateForm: true,
                    validateNonRequired: false,
                })
            );
        },
        handleSave: () => {
            dispatch(
                operations.handleSubmit(true, null, "saveAndContinue", {
                    validateDocs: false,
                    validateForm: true,
                    validateNonRequired: true,
                })
            );
        },
        handleSaveAndExit: () => {
            return dispatch(
                operations.handleSubmit(true, null, "saveAndExit", {
                    validateDocs: false,
                    validateForm: true,
                    validateNonRequired: true,
                })
            );
        },
        toggleSnackBar: (open, variant, message) => {
            dispatch(utils.operations.snackbar(open, variant, message));
        },
        handleAutosaveNotSuccess: (isSaving, err) => {
            dispatch(operations.handleAutosaveNotSuccess(isSaving, err));
        },
        handleAutosaveSuccess: () => {
            dispatch(operations.handleAutosaveSuccess());
        },
        handleAddBrokerCommission: () => {
            dispatch(operations.handleAddBrokerCommission());
        },
        handleCreateOutsideSplitDraft: (enableFreeFormSearch, commissionType) => {
            dispatch(operations.createOutsideSplitDraft(enableFreeFormSearch, commissionType));
        },
        handleSaveOutsideSplit: (key, split) => {
            dispatch(operations.saveOutsideSplit(key, split));
        },
        handleDeleteOutsideSplit: (splitKey) => {
            dispatch(operations.deleteOutsideSplit(splitKey));
        },
        toggleOutsideSplitModal: (options) => {
            dispatch(operations.toggleOutsideSplitModal(options));
        },
        handleAddRunner: (parent) => {
            dispatch(operations.handleAddRunner(parent));
        },
        handleRemoveCommissionOrRunner: (options) => {
            dispatch(operations.handleRemoveCommissionOrRunner(options.key, options.parent, options.outside));
        },
        updateSplit: (splitKey, field, value, source) => {
            dispatch(operations.updateSplit(splitKey, field, value, source));
        },
        updateRunner: (parent, key, field, value) => {
            dispatch(operations.updateRunner(parent, key, field, value));
        },
        toggleComments: () => {
            dispatch(commentsOperations.toggleComments());
        },
        handleVoucherChange: (event) => {
            dispatch(operations.voucherChange(event.target.id, event.target.value));
        },
        handleCheckboxClick: (name, event) => {
            dispatch(operations.handleInput("voucher." + name, event.target.checked));
        },
        handleVoucherChangeAndCalculateSplits: (km, event) => {
            dispatch(operations.voucherChangeAndCalculateSplits(event.target.id, event.target.value, km));
        },
        handleSearchPerson: (inputValue) => {
            dispatch(operations.searchPerson(inputValue));
        },
        handleSearchOutsideBroker: (inputValue) => {
            dispatch(searchOperations.searchOutsideBroker(inputValue));
        },
        handleSearchCompany: (inputValue, companyType) => {
            dispatch(searchOperations.searchCompany(inputValue, companyType));
        },
        handleSearchBroker: (inputValue) => {
            dispatch(searchOperations.autoCompleteBroker(inputValue));
        },
        toggleConfirm: (onSubmit) => {
            dispatch(utils.operations.toggleConfirm(onSubmit));
        },
        handleInput: (path, value) => {
            dispatch(operations.handleInput("voucher." + path, value));
        },
        handleReducerLevelInput: (path, value) => {
            dispatch(operations.handleInput(path, value));
        },
        handleClearDealState: () => {
            dispatch(
                operations.handleInput("voucher", {
                    deal: {},
                    brokerCommissions: {},
                })
            );
            dispatch(operations.clearErrors());
        },
        handleAddArrayElement: (path, object) => {
            dispatch(operations.addArrayElement("voucher." + path, object));
        },
        handleDeleteArrayElement: (path, index) => {
            dispatch(operations.deleteArrayElement("voucher." + path, index));
        },
        handleUpdateArrayElement: (arrayPath, index, field, value) => {
            dispatch(operations.handleInput("voucher." + arrayPath + "[" + index + "]." + field, value));
        },
        handleCalculateSplits: () => {
            dispatch(operations.handleCalculateSplits());
        },
        openModal: (isOpen, dealKey) => {
            dispatch(operations.openModal(isOpen, dealKey));
        },
        deleteDeal: (dealKey) => {
            return dispatch(operations.handleDeleteDeal(dealKey));
        },
        handleChooseDisplayDeleteDialog: (isOpen, dealKey) => {
            dispatch(operations.displayDeleteDialog(isOpen, dealKey));
        },
        togglePropertySearchModal: (isOpen) => {
            dispatch(operations.togglePropertySearchModal(isOpen));
        },
        clearPropertyUpdate: () => {
            dispatch(operations.clearPropertyUpdate());
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ConsultingDealContainer);
